import React, { useState, useEffect } from 'react';
import TransactionsTab from './TransactionsTab';
import AdministratorTab from './AdministratorTab';
import ReportsTab from './ReportsTab';
import SalesInvoice from './SalesInvoice';
import Sidebar from './Sidebar';
import { Menu, LogOut, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIsAndroid } from '@/hooks/use-android';
import { useBackNavigation } from '@/hooks/use-back-navigation';

interface DashboardProps {
  username: string;
  onLogout: () => void;
  token: string;
}

const Dashboard = ({ username, onLogout, token }: DashboardProps) => {
  const [currentPage, setCurrentPage] = useState('transactions');
  const [showSalesPage, setShowSalesPage] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isTransactionsSubpage, setIsTransactionsSubpage] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [activeSubpage, setActiveSubpage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const isAndroid = useIsAndroid();

  // Use back navigation hook for dashboard - this will show exit popup when on dashboard (both Android and iOS)
  // When showSalesPage or activeSubpage is true, we provide a custom handler to close sub-components first
  useBackNavigation({
    canGoBack: true,
    showExitConfirmation: true, // Show exit confirmation on both Android and iOS
    onMobileWebExitConfirm: onLogout, // Mobile web: OK on exit popup -> show login
    onBack: (showSalesPage || activeSubpage) ? () => {
      if (showSalesPage) {
        // When SalesInvoice is shown, back button should close SalesInvoice and return to transactions
        console.log('🔄 Dashboard: Back button pressed while SalesInvoice is open, closing SalesInvoice');
        setShowSalesPage(false);
        setCurrentPage('transactions');
      } else if (activeSubpage) {
        // When a subpage is shown (Refunds, Suppliers, Inventory, Customers), close it
        // TransactionsTab owns showRefunds/showSuppliers/etc. - dispatch event to close
        console.log('🔄 Dashboard: Back button pressed while subpage is open, closing subpage:', activeSubpage);
        window.dispatchEvent(new CustomEvent('resetTransactionsToMain'));
      }
      // Always return 'handled' to indicate we handled the back navigation
      // The main back navigation logic will handle exit confirmation when no subpage is open
    } : undefined
  });

  // Handle URL query parameters for initial tab selection (Daily Stats hidden)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    
    if (tabParam && ['transactions', 'administrator', 'reports'].includes(tabParam)) {
      console.log('🎯 Dashboard: Setting initial page from URL parameter:', tabParam);
      setCurrentPage(tabParam);
    } else if (tabParam === 'daily-stats') {
      setCurrentPage('transactions');
      navigate('/?tab=transactions', { replace: true });
    }

    // Handle showRefunds state from ReturnProcessPage navigation (fallback)
    if (location.state?.showRefunds) {
      console.log('🔄 Dashboard: Showing refunds screen from navigation state (fallback)');
      setCurrentPage('transactions');
    }
  }, [location.search, location.state, navigate]);

  // Handle responsive sidebar behavior
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      // Close sidebar on mobile when screen size changes
      if (mobile) {
        setIsSidebarOpen(false);
      }
    };

    // Set initial state based on screen size
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Add event listener for page changes
  useEffect(() => {
    const handlePageChange = (event: CustomEvent) => {
      const newPage = event.detail;
      console.log('🔄 Dashboard: Received setCurrentPage event:', newPage);
      setCurrentPage(newPage);
      setShowSalesPage(false);
      // Store the current page in sessionStorage to persist across navigation
      sessionStorage.setItem('currentPage', newPage);
    };

    const handleSidebarState = (event: CustomEvent) => {
      const { isOpen, isCollapsed } = event.detail;
      setIsSidebarOpen(isOpen);
      setIsSidebarCollapsed(isCollapsed);
    };

    // Only use URL tab param on mount - do NOT use sessionStorage (so login and hard refresh show Transactions)
    const searchParams = new URLSearchParams(location.search);
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl === 'daily-stats') {
      setCurrentPage('transactions');
      sessionStorage.setItem('currentPage', 'transactions');
    } else if (tabFromUrl && ['transactions', 'administrator', 'reports'].includes(tabFromUrl)) {
      setCurrentPage(tabFromUrl);
      sessionStorage.setItem('currentPage', tabFromUrl);
    }

      // Check for navigation state indicating return to sales invoice
  console.log('Dashboard: Checking location.state:', location.state);
  if (location.state?.returnToSalesInvoice || (location.state?.productAdded && location.state?.scrollToSection === 'products')) {
    console.log('Dashboard: Auto-navigating to sales invoice');
    // Auto-navigate to sales invoice
    setShowSalesPage(true);
    setCurrentPage('transactions');
  }

    // Legacy support for old session storage (can be removed later)
    const returnToSalesInvoice = sessionStorage.getItem('returnToSalesInvoice');
    if (returnToSalesInvoice === 'true') {
      // Clear the flag
      sessionStorage.removeItem('returnToSalesInvoice');
      // Auto-navigate to sales invoice
      setShowSalesPage(true);
      setCurrentPage('transactions'); // Ensure we're on the right page
    }

    window.addEventListener('setCurrentPage', handlePageChange as EventListener);
    window.addEventListener('setSidebarState', handleSidebarState as EventListener);
    // Listen for transactions subpage visibility changes (e.g., Customers, Inventory)
    const handleTransactionsSubpage = (event: Event) => {
      try {
        const custom = event as CustomEvent;
        setIsTransactionsSubpage(!!custom.detail?.isSubpage);
      } catch {
        setIsTransactionsSubpage(false);
      }
    };
    window.addEventListener('transactionsSubpageChange', handleTransactionsSubpage as EventListener);

    // Listen for sidebar close event from back button
    const handleCloseSidebar = () => {
      setIsSidebarOpen(false);
    };
    window.addEventListener('closeSidebar', handleCloseSidebar as EventListener);

    return () => {
      window.removeEventListener('setCurrentPage', handlePageChange as EventListener);
      window.removeEventListener('setSidebarState', handleSidebarState as EventListener);
      window.removeEventListener('transactionsSubpageChange', handleTransactionsSubpage as EventListener);
      window.removeEventListener('closeSidebar', handleCloseSidebar as EventListener);
    };
  }, []);

  // Function to determine if header should be shown (Daily Stats hidden)
  const shouldShowHeader = () => {
    // Only show header for main sidebar tabs, not for sub-pages
    const mainTabs = ['transactions', 'administrator', 'reports'];
    // Hide header when a transactions subpage (like CustomerPage) is open
    const isTransactionsHeaderHidden = currentPage === 'transactions' && isTransactionsSubpage;
    return mainTabs.includes(currentPage) && !showSalesPage && !isTransactionsHeaderHidden;
  };

  const renderContent = () => {
    if (showSalesPage) {
      return <SalesInvoice />;
    }

    switch (currentPage) {
      case 'transactions':
        return <TransactionsTab 
          onSalesClick={() => {
            // Clear cache when showing Sales Invoice
            queryClient.clear();
            setShowSalesPage(true);
          }} 
          token={token}
          onSubpageChange={(subpage) => setActiveSubpage(subpage)}
        />;
      case 'administrator':
        return <AdministratorTab />;
      case 'reports':
        return <ReportsTab />;
      case 'daily-stats':
        return <TransactionsTab onSalesClick={() => { queryClient.clear(); setShowSalesPage(true); }} token={token} />;
      default:
        // Instead of defaulting to transactions, use the stored page or transactions
        const storedPage = sessionStorage.getItem('currentPage');
        if (storedPage === 'reports') {
          return <ReportsTab />;
        }
        return <TransactionsTab onSalesClick={() => {
          // Clear cache when showing Sales Invoice
          queryClient.clear();
          setShowSalesPage(true);
        }} token={token} />;
    }
  };

  return (
    <div className="min-h-screen bg-white dashboard-screen" data-screen="dashboard">
      {/* Mobile Sidebar Toggle Button - Only show when header is visible and sidebar is closed */}
      {shouldShowHeader() && !isSidebarOpen && (
        <Button
          variant="ghost"
          size="icon"
          className="fixed top-8 left-4 z-50 lg:hidden text-gray-600 hover:bg-gray-100 bg-white/90 backdrop-blur-sm border border-gray-200 shadow-sm h-8 w-8"
          style={{ top: 'calc(var(--safe-area-top) + 2rem)' }}
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        >
          <Menu className="h-4 w-4" />
        </Button>
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-40 transform transition-transform duration-300 ease-in-out
        ${isMobile ? (!isSidebarOpen ? '-translate-x-full' : 'translate-x-0') : 'translate-x-0'}
        lg:translate-x-0
        ${isMobile ? 'w-80' : ''}
      `}>
        <Sidebar
          currentPage={currentPage}
          onNavigate={(page) => {
            // Clear cache when switching between major sections
            if (page !== currentPage) {
              queryClient.clear();
            }
            setCurrentPage(page);
            setShowSalesPage(false);
            // Update URL with tab so browser back from report subpages returns to correct tab
            navigate(`/?tab=${page}`, { replace: true });
            // When clicking Transactions tab, reset to main grid (in case user was on a subpage like Refunds)
            if (page === 'transactions') {
              window.dispatchEvent(new CustomEvent('resetTransactionsToMain'));
            }
            // Close sidebar on mobile after navigation
            if (isMobile) {
              setIsSidebarOpen(false);
            }
          }}
          isCollapsed={isSidebarCollapsed}
          onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          username={username}
        />
      </div>

      {/* Overlay for mobile sidebar */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      {/* When sidebar is collapsed on desktop, reduce left padding so content sits closer to the edge */}
      <div className={`transition-all duration-300 ${isSidebarCollapsed ? 'lg:pl-16 xl:pl-16 2xl:pl-16' : 'lg:pl-56 xl:pl-64 2xl:pl-72'} w-full flex flex-col h-screen`}>
        {/* Fixed Header - Only show for main sidebar tabs */}
        {shouldShowHeader() && (
          <div className="bg-white border-b border-gray-200 android-header-safe-mobile sm:android-header-safe flex-shrink-0">
            <div className="px-2 sm:px-6 lg:px-6 xl:px-8">
              <div className="flex flex-row justify-between items-center py-4 sm:py-4 lg:py-4 xl:py-4 sm:h-24 lg:h-20 xl:h-24">
                <div className="flex items-center space-x-3 sm:space-x-6 lg:space-x-4 xl:space-x-6 ml-12 lg:ml-0 flex-1 min-w-0">
                  <div className="min-w-0">
                    <h1 className="text-lg sm:text-3xl lg:text-2xl xl:text-3xl font-bold text-gray-900 truncate">
                      {currentPage === 'transactions' && (isAndroid ? 'Transaction' : 'Transaction Management')}
                      {currentPage === 'administrator' && 'Administrator'}
                      {currentPage === 'reports' && 'Reports'}
                    </h1>
                  </div>
                </div>
                <div className="flex items-center space-x-3 sm:space-x-6 lg:space-x-4 xl:space-x-6 flex-shrink-0">
                  {/* Welcome message - hidden on Android */}
                  {!isAndroid && (
                    <div className="text-right">
                      <span className="text-gray-500 text-xs sm:text-sm lg:text-xs xl:text-sm block">Welcome back,</span>
                      <span className="text-gray-900 font-semibold text-sm sm:text-lg lg:text-base xl:text-lg truncate max-w-[120px] sm:max-w-none inline-block">{username}</span>
                    </div>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 rounded-full bg-gray-100 hover:bg-gray-200 border border-gray-300"
                    onClick={() => setShowLogoutDialog(true)}
                  >
                    <LogOut className="h-4 w-4 text-gray-700" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Scrollable Content Area */}
        <div 
          className={`flex-1 overflow-y-auto ${shouldShowHeader() ? 'p-4 lg:p-6 xl:p-8' : 'pt-0'}`}
          data-screen={!shouldShowHeader() ? 'subpage' : 'dashboard'}
          data-has-back-button={!shouldShowHeader() ? 'true' : 'false'}
        >
          {/* Add safe area padding for sub-pages when header is not shown */}
          {!shouldShowHeader() && (
            <div className="w-full bg-transparent android-subpage-safe" />
          )}
          {renderContent()}
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent className="rounded-xl shadow-2xl border-0 bg-white">
          <AlertDialogHeader className="pb-4">
            <AlertDialogTitle className="text-xl font-bold text-gray-900">Log Out?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600 mt-2">
              Are you sure want to log out?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3">
            <AlertDialogCancel className="w-full sm:w-auto rounded-lg border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-200 ease-in-out transform hover:scale-105">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowLogoutDialog(false);
                onLogout();
              }}
              className="w-full sm:w-auto rounded-lg bg-orange-600 hover:bg-orange-700 text-white font-medium transition-all duration-200 ease-in-out transform hover:scale-105 active:scale-95"
            >
              Log out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Dashboard;
